import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { PencilSquareIcon, MicrophoneIcon, PhotoIcon, ShareIcon } from '@heroicons/react/24/outline';
import AudioNoteRecorder from './AudioNoteRecorder';

const StudyNoteEditor = ({
  isOpen,
  onClose,
  note = null, // null for new note, existing note for editing
  book,
  chapter,
  verse = null,
  verseRange = null,
  onSave,
  isFavorited = false,
  onToggleFavorite = null,
}) => {
  const { currentUser } = useAuth();
  const [noteType, setNoteType] = useState('written');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [audioData, setAudioData] = useState(null);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [imageFile, setImageFile] = useState(null);       // File object for upload
  const [imagePreview, setImagePreview] = useState(null); // Object URL for preview
  const [existingImageUrl, setExistingImageUrl] = useState(null); // URL for already-saved image
  const imageInputRef = useRef(null);

  // Initialize form with existing note data if editing
  useEffect(() => {
    if (note) {
      setNoteType(note.noteType || 'written');
      setContent(note.content || '');
      setAudioData(note.audioUrl ? {
        audioUrl: note.audioUrl,
        audioType: note.audioType || 'audio/mp4',
        duration: note.audioDuration || 0,
        fileSize: note.fileSize || 0
      } : null);
      setExistingImageUrl(note.imageUrl || null);
      setImageFile(null);
      setImagePreview(null);
    } else {
      setNoteType('written');
      setContent('');
      setAudioData(null);
      setExistingImageUrl(null);
      setImageFile(null);
      setImagePreview(null);
    }
    setError('');
    setShowAudioRecorder(false);
  }, [note, isOpen]);

  // Clean up object URL on unmount or when imageFile changes
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setExistingImageUrl(null);
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleSave = async () => {
    console.log('🚀 StudyNoteEditor: Starting save process...');
    
    if (!content.trim() && !audioData && !imageFile && !existingImageUrl) {
      setError('Please enter some content, record audio, or pick an image');
      return;
    }

    if (!currentUser) {
      console.log('❌ StudyNoteEditor: No current user');
      setError('You must be logged in to save notes');
      return;
    }

    if (!currentUser.familyIds || currentUser.familyIds.length === 0) {
      console.log('❌ StudyNoteEditor: User has no family access');
      setError('You must be part of a family to save notes');
      return;
    }

    console.log('📋 StudyNoteEditor: Current user data:', {
      uid: currentUser.uid,
      displayName: currentUser.displayName,
      firstName: currentUser.firstName,
      email: currentUser.email,
      familyIds: currentUser.familyIds
    });

    console.log('📖 StudyNoteEditor: Note data:', {
      book,
      chapter,
      verse,
      verseRange,
      noteType,
      content: content.trim(),
      isEditing: !!note
    });

    setIsSaving(true);
    setError('');

    try {
      const noteData = {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.firstName || currentUser.email,
        userDisplayName: currentUser.displayName || currentUser.firstName || 'Family Member',
        book: book,
        chapter: chapter,
        verse: verse,
        verseRange: verseRange,
        noteType: noteType,
        content: content.trim(),
        createdAt: note ? note.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPrivate: false,
        tags: []
      };

      // Add audio data if present
      if (audioData) {
        noteData.audioUrl = audioData.audioUrl;
        noteData.audioType = audioData.audioType;
        noteData.audioDuration = audioData.duration;
        noteData.fileSize = audioData.fileSize;
      }

      // Upload image if a new file was picked
      if (imageFile) {
        const ext = imageFile.name.split('.').pop() || 'jpg';
        const filename = `note_${Date.now()}.${ext}`;
        const storageRef = ref(storage, `study_notes/${currentUser.uid}/${filename}`);
        await uploadBytes(storageRef, imageFile, { contentType: imageFile.type });
        noteData.imageUrl = await getDownloadURL(storageRef);
        noteData.imageType = imageFile.type;
        noteData.imageSize = imageFile.size;
      } else if (existingImageUrl) {
        noteData.imageUrl = existingImageUrl;
      }

      console.log('💾 StudyNoteEditor: Prepared note data for Firestore:', noteData);

      // Save note to user's personal notes collection
      console.log(`💾 StudyNoteEditor: Saving note to user's personal collection...`);
      
      let noteId;
      if (note) {
        // Update existing note
        console.log(`🔄 StudyNoteEditor: Updating existing note:`, note.id);
        const noteRef = doc(db, `users/${currentUser.uid}/notes`, note.id);
        await updateDoc(noteRef, noteData);
        console.log(`✅ StudyNoteEditor: Note updated successfully`);
        noteId = note.id;
      } else {
        // Create new note in user's personal collection
        console.log(`➕ StudyNoteEditor: Creating new note in user's personal collection...`);
        const notesRef = collection(db, `users/${currentUser.uid}/notes`);
        const docRef = await addDoc(notesRef, noteData);
        console.log(`✅ StudyNoteEditor: Note created with ID:`, docRef.id);
        console.log(`✅ StudyNoteEditor: Note data saved:`, {
          userId: noteData.userId,
          userName: noteData.userName,
          userDisplayName: noteData.userDisplayName,
          book: noteData.book,
          chapter: noteData.chapter,
          verse: noteData.verse,
          noteType: noteData.noteType
        });
        noteId = docRef.id;

        // Trigger study note notification Cloud Function (only on new notes)
        const familyIds = currentUser.familyIds || [];
        if (familyIds.length) {
          addDoc(collection(db, 'study_notes'), {
            ...noteData,
            noteId,
            familyIds,
          }).catch(() => {});
        }
      }

      console.log('🎉 StudyNoteEditor: Save completed successfully, calling onSave callback');
      onSave && onSave();
      onClose();
    } catch (error) {
      console.error('❌ StudyNoteEditor: Error saving study note:', error);
      console.error('❌ StudyNoteEditor: Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      setError('Failed to save note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle sharing note
  const handleShare = async () => {
    if (!note) {
      setError('Please save the note first before sharing');
      return;
    }

    try {
      // Mark the note shareable so it can be viewed publicly via its link
      await updateDoc(doc(db, `users/${currentUser.uid}/notes`, note.id), { isShared: true });

      // Get the base URL - use environment variable in production, fallback to current origin
      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const shareUrl = `${baseUrl}/notes/${currentUser.uid}/${note.id}`;
      await navigator.clipboard.writeText(shareUrl);

      // Show success message (you could add a toast notification here)
      alert('Share link copied to clipboard!');
    } catch (error) {
      console.error('❌ Error copying share link:', error);
      setError('Failed to copy share link. Please try again.');
    }
  };

  const handleCancel = () => {
    setContent('');
    setAudioData(null);
    clearImage();
    setError('');
    setShowAudioRecorder(false);
    onClose();
  };

  // Handle audio recording completion
  const handleAudioRecordingComplete = (audioInfo) => {
    console.log('🎵 StudyNoteEditor: Audio recording completed:', audioInfo);
    setAudioData(audioInfo);
    setShowAudioRecorder(false);
    setNoteType('audio');
  };

  // Handle audio recording cancellation
  const handleAudioRecordingCancel = () => {
    console.log('❌ StudyNoteEditor: Audio recording cancelled');
    setShowAudioRecorder(false);
  };

  if (!isOpen) return null;

  const getVerseDisplay = () => {
    if (verseRange) {
      return `${verseRange.start}-${verseRange.end}`;
    } else if (verse) {
      return verse.toString();
    }
    return 'Chapter';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-space-cadet text-white p-4 rounded-t-lg">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">
                {note ? 'Edit Note' : 'Add Study Note'}
              </h2>
              <p className="text-sm opacity-90">
                {book} {chapter}:{getVerseDisplay()}
              </p>
            </div>
            {onToggleFavorite && (
              <button
                type="button"
                onClick={onToggleFavorite}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0 ml-3"
              >
                <svg className="w-5 h-5" fill={isFavorited ? '#ef4444' : 'none'} stroke="#ef4444" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Note Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-space-cadet mb-2">
              Note Type
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setNoteType('written')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  noteType === 'written'
                    ? 'bg-cerulean text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <PencilSquareIcon className="w-4 h-4" /> Written
              </button>
              <button
                type="button"
                onClick={() => {
                  if (noteType === 'audio') {
                    setNoteType('written');
                    setAudioData(null);
                  } else {
                    setNoteType('audio');
                    setContent('');
                  }
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  noteType === 'audio'
                    ? 'bg-cerulean text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <MicrophoneIcon className="w-4 h-4" /> Audio
              </button>
              <button
                type="button"
                onClick={() => {
                  if (noteType === 'image') {
                    setNoteType('written');
                    clearImage();
                  } else {
                    setNoteType('image');
                    setContent('');
                  }
                }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  noteType === 'image'
                    ? 'bg-cerulean text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <PhotoIcon className="w-4 h-4" /> Image
              </button>
            </div>
          </div>

          {/* Content Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-space-cadet mb-2">
              {noteType === 'written' ? 'Your Note' : noteType === 'audio' ? 'Audio Note' : 'Image Note'}
            </label>
            {noteType === 'written' ? (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your thoughts, insights, or memories about this passage..."
                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cerulean focus:border-transparent resize-none"
                maxLength={1000}
              />
            ) : noteType === 'audio' ? (
              <div className="space-y-4">
                {!audioData && !showAudioRecorder && (
                  <div className="w-full h-32 p-3 border border-gray-300 rounded-lg bg-gray-50 flex flex-col items-center justify-center text-gray-500">
                    <svg className="w-8 h-8 mb-2 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                    </svg>
                    <p className="text-sm mb-3">No audio recorded yet</p>
                    <button
                      onClick={() => setShowAudioRecorder(true)}
                      className="px-4 py-2 bg-cerulean text-white rounded-lg hover:bg-space-cadet transition-colors text-sm font-medium flex items-center gap-1.5"
                    >
                      <MicrophoneIcon className="w-4 h-4" /> Record Audio
                    </button>
                  </div>
                )}
                
                {showAudioRecorder && (
                  <AudioNoteRecorder
                    onRecordingComplete={handleAudioRecordingComplete}
                    onCancel={handleAudioRecordingCancel}
                    maxDuration={300} // 5 minutes
                  />
                )}
                
                {audioData && (
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Audio Preview</span>
                        <button
                          onClick={() => {
                            setAudioData(null);
                            setShowAudioRecorder(false);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove audio"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <audio controls className="w-full" playsInline>
                        <source src={audioData.audioUrl} type={audioData.audioType} />
                        Your browser does not support the audio element.
                      </audio>
                      <div className="text-xs text-gray-500 mt-1">
                        Duration: {Math.round(audioData.duration)}s • Size: {(audioData.fileSize / 1024 / 1024).toFixed(2)}MB
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setShowAudioRecorder(true)}
                      className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                      <MicrophoneIcon className="w-4 h-4" /> Record New Audio
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Hidden file input */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImagePick}
                />

                {/* Image preview or picker */}
                {(imagePreview || existingImageUrl) ? (
                  <div className="space-y-2">
                    <div className="relative rounded-xl overflow-hidden border border-gray-200">
                      <img
                        src={imagePreview || existingImageUrl}
                        alt="Note"
                        className="w-full object-contain max-h-64"
                      />
                      <button
                        onClick={clearImage}
                        className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                        title="Remove image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      className="w-full px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                    >
                      <PhotoIcon className="w-4 h-4" /> Choose a different image
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    className="w-full h-36 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-500 hover:border-cerulean hover:text-cerulean transition-colors"
                  >
                    <PhotoIcon className="w-8 h-8" />
                    <span className="text-sm font-medium">Tap to choose a photo</span>
                    <span className="text-xs text-gray-400">Photo of Bible margins, handwritten notes, etc.</span>
                  </button>
                )}

                {/* Optional caption */}
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Add a caption (optional) — e.g. Dad's notes from 1978"
                  className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cerulean focus:border-transparent resize-none text-sm"
                  maxLength={500}
                />
              </div>
            )}
            {noteType === 'written' && (
              <p className="text-xs text-gray-500 mt-1">
                {content.length}/1000 characters
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
            {note && (
              <button
                onClick={handleShare}
                className="px-4 py-2 bg-aquamarine text-space-cadet rounded-lg hover:bg-cerulean hover:text-white transition-colors font-medium flex items-center gap-1.5"
                title="Copy share link"
              >
                <ShareIcon className="w-4 h-4" /> Share
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || (!content.trim() && !audioData && !imageFile && !existingImageUrl)}
              className="flex-1 px-4 py-2 bg-cerulean text-white rounded-lg hover:bg-space-cadet transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : (note ? 'Update Note' : 'Save Note')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyNoteEditor;
